import { useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import type { ConversionRatesOverviewListParams } from "@similarweb/pro-api-client";
import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Card, IconButton, PixelPlaceholderLoader, ProgressBar, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { CHART_COLORS } from "app/constants/ChartColors";
import { percentageSignFilter } from "app/filters/ngFilters";
import { openHooksModalV5 } from "app/services/ModalService";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { ConversionRateEmptyState } from "../../single/ConversionRate/ConversionRateEmptyState";
import { useConversionRateAvailability } from "app/pages/website-analysis/TrafficAndEngagement/ConversionsOverTime/useConversionRateAvailability";
import { useConversionRateData } from "../../single/ConversionRate/useConversionRateData";
import { CompareCardHeader } from "../common/CompareCardHeader";
import { DOMAIN_COLUMN_MIN_WIDTH, NOT_AVAILABLE_VALUE } from "../common/constants";
import { getCountryText } from "../common/getCountryText";
import { DomainWithTooltipLink } from "../common/DomainWithTooltipLink";

interface ICompareConversionRateProps {
    params: {
        country: number | string;
        from: string;
        to: string;
        key: string;
        isWindow: boolean;
        isWWW: boolean;
    };
}

const TITLE_KEY = "wa.ao.conversionrate";
const TOOLTIP_KEY = "wa.ao.conversionrate.tooltip";
const VALUE_COLUMN_WIDTH = "40px";

const formatPercent = percentageSignFilter();

const formatMonthYear = (value?: string) => {
    if (!value) {
        return null;
    }

    const normalizedValue = value.includes("|") ? value.split("|").join("-") : value;
    const parsedDate = dayjs(normalizedValue);
    return parsedDate.isValid() ? parsedDate.format(shortMonthAndYear) : null;
};

export const ConversionRate = ({ params }: ICompareConversionRateProps) => {
    const { translate } = useTranslate();

    const countryCode = Number(params.country);
    const countryText = getCountryText(countryCode);

    const dateFromText = formatMonthYear(params.from);
    const dateToText = formatMonthYear(params.to);
    const dateRangeText = dateFromText && dateToText ? `${dateFromText} - ${dateToText}` : NOT_AVAILABLE_VALUE;

    const comparedDomains = useMemo(
        () =>
            params.key
                .split(",")
                .map((domain) => domain.trim())
                .filter(Boolean),
        [params.key],
    );

    const { state: availabilityState, isLoading: isAvailabilityLoading } = useConversionRateAvailability({
        keys: comparedDomains,
        isWWW: params.isWWW,
    });

    const shouldFetchData =
        availabilityState === "available" &&
        !!params.from &&
        !!params.to &&
        params.isWWW !== undefined &&
        comparedDomains.length > 0;

    const apiParams = useMemo(
        () =>
            ({
                country: countryCode,
                from: params.from,
                to: params.to,
                keys: comparedDomains,
                isWindow: params.isWindow,
                includeSubdomains: !params.isWWW,
            }) satisfies ConversionRatesOverviewListParams,
        [countryCode, params.from, params.to, params.isWindow, params.isWWW, comparedDomains],
    );

    const {
        data,
        isLoading: isDataLoading,
        isError,
        emptyStateType,
    } = useConversionRateData(apiParams, {
        enabled: shouldFetchData,
    });

    const isLoading = isAvailabilityLoading || isDataLoading;

    const rows = useMemo(() => {
        const conversionRateByDomain = new Map((data ?? []).map((item) => [item.site.toLowerCase(), item] as const));

        return comparedDomains.map((domain, index) => {
            const item = conversionRateByDomain.get(domain.toLowerCase());
            const itemAsRecord = (item ?? {}) as Record<string, unknown>;
            const apiFavicon =
                typeof itemAsRecord.favicon === "string"
                    ? itemAsRecord.favicon
                    : typeof itemAsRecord.Favicon === "string"
                      ? itemAsRecord.Favicon
                      : "";
            const fallbackFavicon = domain ? getFaviconUrl(domain) : "";

            return {
                domain,
                color: CHART_COLORS.compareMainColors[index % CHART_COLORS.compareMainColors.length],
                conversionRate: item?.conversionRate ?? null,
                favicon: apiFavicon || fallbackFavicon,
            };
        });
    }, [data, comparedDomains]);

    const isEmpty = rows.length === 0;
    const hasNoData = rows.length > 0 && rows.every((row) => row.conversionRate == null);

    const titleAccessory = (
        <Flex alignItems="center">
            <Badge type="new" />
            <Tooltip content={translate(TOOLTIP_KEY)}>
                <Info24px
                    size={24}
                    color={colorsPalettes.carbon[200]}
                />
            </Tooltip>
        </Flex>
    );

    if (availabilityState === "hidden") {
        return null;
    }

    if (availabilityState === "locked") {
        return (
            <Card data-automation="conversion-rate-compare-widget">
                <CompareCardHeader
                    title={translate(TITLE_KEY)}
                    tooltipText={translate(TOOLTIP_KEY)}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                    titleAccessory={titleAccessory}
                    dataAutomation="compare-conversion-rate"
                />
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                    padding="24px"
                    gap="8px"
                    flex="1"
                >
                    <Text fontVariant="subtitle2">{translate("wa.ao.conversionrate.locked.title")}</Text>
                    <IconButton
                        type="trial"
                        iconName="sparkles"
                        onClick={() => openHooksModalV5("conversionRate")}
                        data-automation="conversion-rate-locked-cta"
                    >
                        {translate("wa.ao.conversionrate.locked.cta")}
                    </IconButton>
                </Flex>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card data-automation="conversion-rate-compare-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                    data-automation="conversion-rate-loading"
                >
                    <PixelPlaceholderLoader
                        width="40%"
                        height={16}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                    <PixelPlaceholderLoader
                        width="100%"
                        height={20}
                    />
                </Flex>
            </Card>
        );
    }

    if (isError || isEmpty || hasNoData) {
        return (
            <Card data-automation="conversion-rate-compare-widget">
                <CompareCardHeader
                    title={translate(TITLE_KEY)}
                    tooltipText={translate(TOOLTIP_KEY)}
                    dateRangeText={dateRangeText}
                    countryCode={countryCode}
                    countryText={countryText}
                    titleAccessory={titleAccessory}
                    dataAutomation="compare-conversion-rate"
                />
                <ConversionRateEmptyState emptyStateType={emptyStateType} />
            </Card>
        );
    }

    return (
        <Card data-automation="conversion-rate-compare-widget">
            <CompareCardHeader
                title={translate(TITLE_KEY)}
                tooltipText={translate(TOOLTIP_KEY)}
                dataAutomation="compare-conversion-rate"
                dateRangeText={dateRangeText}
                countryCode={countryCode}
                countryText={countryText}
                titleAccessory={titleAccessory}
            />
            <Flex
                flexDirection="column"
                gap="16px"
                padding="16px 24px"
                flex="1"
                data-automation="conversion-rate-table"
            >
                <Flex
                    alignItems="center"
                    gap="12px"
                    data-automation="conversion-rate-table-header"
                >
                    <Flex
                        flex="2"
                        minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                    >
                        <Text
                            fontVariant="captionTitle"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("global.domain")}
                        </Text>
                    </Flex>
                    <Flex
                        flex="3"
                        minWidth="0"
                        alignItems="center"
                        gap="12px"
                    >
                        <Flex
                            flex="1"
                            minWidth="0"
                        />
                    </Flex>
                </Flex>
                {rows.map((row) => {
                    const formattedValue =
                        row.conversionRate == null
                            ? NOT_AVAILABLE_VALUE
                            : String(formatPercent(row.conversionRate, 1) ?? NOT_AVAILABLE_VALUE);

                    return (
                        <Flex
                            key={row.domain}
                            alignItems="center"
                            gap="12px"
                            data-automation="conversion-rate-row"
                        >
                            <Flex
                                flex="2"
                                minWidth={DOMAIN_COLUMN_MIN_WIDTH}
                                alignItems="center"
                            >
                                <DomainWithTooltipLink
                                    domain={row.domain}
                                    favicon={row.favicon}
                                />
                            </Flex>
                            <Flex
                                flex="3"
                                minWidth="0"
                                alignItems="center"
                                gap="12px"
                            >
                                <Flex
                                    flex="1"
                                    minWidth="0"
                                >
                                    <ProgressBar
                                        data-automation="conversion-rate-progress-bar"
                                        values={[row.conversionRate ?? 0]}
                                        colorSet={[row.color]}
                                    />
                                </Flex>
                                <Flex
                                    width={VALUE_COLUMN_WIDTH}
                                    maxWidth={VALUE_COLUMN_WIDTH}
                                    justifyContent="flex-end"
                                >
                                    <Text
                                        fontVariant="input2"
                                        color={colorsPalettes.carbon[400]}
                                        textAlign="right"
                                        data-automation="conversion-rate-value"
                                    >
                                        {formattedValue}
                                    </Text>
                                </Flex>
                            </Flex>
                        </Flex>
                    );
                })}
            </Flex>
        </Card>
    );
};
